import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Card, CardContent, Typography, Link, Stack } from '@mui/material';
import Logo from '../components/Logo';
import VolverInicio from '../components/VolverInicio';
import MgeekFooter from '../components/MgeekFooter';

/**
 * Páginas legales públicas: Política de Privacidad (/privacidad) y
 * Condiciones del Servicio (/terminos). Google las exige en la pantalla de
 * consentimiento OAuth para conectar Calendar, Sheets y Gmail.
 *
 * Texto base: debe ser revisado por un abogado antes de considerarse definitivo.
 */

const EMPRESA = 'mGeek';
const PRODUCTO = 'tRespondo';
const SITIO = 'https://trespondo.cl';
const CONTACTO = 'contacto@inmobot.cl';
const ACTUALIZADO = '29 de octubre de 2026';

interface Seccion { titulo: string; parrafos: React.ReactNode[] }

const Documento: React.FC<{ titulo: string; secciones: Seccion[] }> = ({ titulo, secciones }) => (
  <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F1F5F9' }}>
    <Container maxWidth="md" sx={{ py: 4, flex: 1 }}>
      <Card>
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <VolverInicio />
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Logo size={36} />
            <Typography variant="h4" component="h1" sx={{ pt: 1 }}>{titulo}</Typography>
            <Typography variant="body2" color="text.secondary">Última actualización: {ACTUALIZADO}</Typography>
          </Stack>
          {secciones.map((s, i) => (
            <Box key={s.titulo} component="section" sx={{ mb: 3 }}>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>{i + 1}. {s.titulo}</Typography>
              {s.parrafos.map((p, j) => (
                <Typography key={j} variant="body1" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>{p}</Typography>
              ))}
            </Box>
          ))}
        </CardContent>
      </Card>
    </Container>
    <MgeekFooter variant="light" />
  </Box>
);

const mail = <Link href={`mailto:${CONTACTO}`}>{CONTACTO}</Link>;

export const Privacidad: React.FC = () => (
  <Documento
    titulo="Política de Privacidad"
    secciones={[
      {
        titulo: 'Quiénes somos',
        parrafos: [
          <>{PRODUCTO} ({SITIO}) es una plataforma desarrollada por {EMPRESA} que permite a empresas y personas crear un asistente con inteligencia artificial que responde a sus clientes por WhatsApp, Instagram, Messenger y su sitio web. Esta política explica qué datos tratamos, para qué y cuáles son tus derechos. Si tienes dudas, escríbenos a {mail}.</>,
        ],
      },
      {
        titulo: 'Datos que recopilamos',
        parrafos: [
          'Datos de tu cuenta: nombre, email, empresa, teléfono y contraseña (guardada cifrada, nunca en texto plano).',
          'Configuración de tu agente: instrucciones, base de conocimiento, flujos, plantillas y horarios que tú cargas.',
          'Conversaciones: los mensajes que tus clientes envían a tus canales conectados y las respuestas del agente, para mostrarlos en tu panel y dar contexto al agente.',
          'Datos de pago: los pagos se procesan a través de Flow. No almacenamos los números completos de tarjetas; solo guardamos referencias del pago y, si activas el cobro automático, el tipo de tarjeta y sus últimos 4 dígitos.',
          'Datos técnicos: registros de uso y errores necesarios para operar y proteger el servicio.',
        ],
      },
      {
        titulo: 'Datos de Google que usamos',
        parrafos: [
          'Si decides conectar tu cuenta de Google, solicitamos solo los permisos de las herramientas que tú activas:',
          'Google Calendar (permiso para crear y gestionar eventos): para que tu agente agende citas en TU calendario cuando un cliente lo solicita.',
          'Google Sheets (permiso de hojas de cálculo): para agregar los datos de tus leads como filas en la planilla que tú eliges.',
          'Gmail (permiso solo para enviar): para enviar correos de notificación desde tu cuenta cuando tú lo configuras.',
          'Datos básicos de perfil (nombre y email de Google): para mostrarte qué cuenta está conectada.',
          'Cada usuario conecta su propia cuenta de Google. Los permisos se guardan asociados únicamente a tu cuenta de tRespondo y se usan solo para ejecutar las acciones que tú configuraste. No leemos tus correos, no leemos tus otros eventos ni planillas más allá de lo necesario para la acción solicitada, y no vendemos ni compartimos tus datos de Google con terceros.',
          <><strong>Uso limitado:</strong> el uso y la transferencia que hace {PRODUCTO} de la información recibida de las API de Google se ajusta a la{' '}<Link href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Política de Datos del Usuario de los Servicios de API de Google</Link>, incluidos los requisitos de Uso Limitado. No usamos los datos de Google para publicidad ni para entrenar modelos generales de inteligencia artificial.</>,
        ],
      },
      {
        titulo: 'Para qué usamos los datos',
        parrafos: [
          'Prestar el servicio: que tu agente responda, agende, registre leads y ejecute tus flujos.',
          'Gestionar tu cuenta, tu suscripción y los pagos.',
          'Enviarte avisos sobre tu cuenta (por ejemplo, vencimientos o recuperación de contraseña).',
          'Mantener la seguridad, prevenir abusos y corregir errores.',
        ],
      },
      {
        titulo: 'Con quién compartimos datos',
        parrafos: [
          'Solo con proveedores necesarios para operar el servicio, que actúan bajo nuestras instrucciones: Amazon Web Services (alojamiento, base de datos, correo y el modelo de inteligencia artificial que genera las respuestas), Meta (WhatsApp, Instagram y Messenger, cuando conectas esos canales), Google (cuando conectas sus herramientas) y Flow (pagos).',
          'Los mensajes se envían al modelo de inteligencia artificial solo para generar la respuesta de tu agente. Nuestro proveedor de IA no usa esos datos para entrenar sus modelos.',
          'No vendemos datos personales. Podemos entregar información si una autoridad competente lo exige conforme a la ley.',
        ],
      },
      {
        titulo: 'Cuánto tiempo guardamos los datos',
        parrafos: [
          'Mientras tu cuenta esté activa. Si cierras tu cuenta, eliminamos o anonimizamos tus datos en un plazo razonable, salvo aquellos que la ley nos obligue a conservar (por ejemplo, registros de pagos).',
        ],
      },
      {
        titulo: 'Tus derechos y cómo desconectar Google',
        parrafos: [
          <>Puedes pedir acceso, corrección o eliminación de tus datos, u oponerte a su tratamiento, escribiendo a {mail}. Respondemos conforme a la legislación chilena de protección de datos personales.</>,
          <>Puedes desconectar Google en cualquier momento desde Integraciones en tu panel, o revocar el acceso directamente en{' '}<Link href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">myaccount.google.com/permissions</Link>. Al desconectar, dejamos de usar esos permisos.</>,
        ],
      },
      {
        titulo: 'Seguridad',
        parrafos: [
          'Usamos conexiones cifradas (HTTPS), contraseñas cifradas y acceso restringido a la base de datos. Ningún sistema es 100% seguro, pero tomamos medidas razonables para proteger tu información.',
        ],
      },
      {
        titulo: 'Cambios a esta política',
        parrafos: [
          'Si hacemos cambios importantes, te avisaremos por email o dentro de la plataforma. La fecha de última actualización aparece al inicio de esta página.',
        ],
      },
    ]}
  />
);

export const Terminos: React.FC = () => (
  <Documento
    titulo="Condiciones del Servicio"
    secciones={[
      {
        titulo: 'Aceptación',
        parrafos: [
          <>Al crear una cuenta o usar {PRODUCTO} ({SITIO}), servicio ofrecido por {EMPRESA}, aceptas estas condiciones y nuestra{' '}<Link component={RouterLink} to="/privacidad">Política de Privacidad</Link>.</>,
        ],
      },
      {
        titulo: 'El servicio',
        parrafos: [
          'tRespondo permite configurar un agente con inteligencia artificial que responde mensajes de tus clientes en los canales que conectes, y que puede agendar citas, registrar leads y ejecutar flujos usando las herramientas que tú autorizas.',
          'Las respuestas las genera una inteligencia artificial y pueden contener errores. Eres responsable de configurar correctamente tu agente y de revisar su funcionamiento.',
        ],
      },
      {
        titulo: 'Tu cuenta',
        parrafos: [
          'Debes entregar datos verdaderos y mantener segura tu contraseña. Eres responsable de la actividad que ocurra en tu cuenta.',
        ],
      },
      {
        titulo: 'Uso aceptable',
        parrafos: [
          'No puedes usar la plataforma para enviar spam, engañar o suplantar a otras personas, ni para actividades ilegales.',
          'Debes cumplir las políticas de los canales que conectas (por ejemplo, las políticas de WhatsApp Business y de Meta) y contar con la autorización de tus clientes para contactarlos.',
          'Podemos suspender cuentas que incumplan estas reglas.',
        ],
      },
      {
        titulo: 'Planes, prueba y pagos',
        parrafos: [
          'Ofrecemos un periodo de prueba gratis. Después, el servicio requiere un plan pagado. Los precios y lo que incluye cada plan se muestran en la plataforma.',
          'Los pagos se procesan a través de Flow. Si no se renueva el pago, el agente deja de responder al terminar el periodo, pero tu configuración se conserva.',
          'Puedes cancelar en cualquier momento; la cancelación se aplica al terminar el periodo ya pagado.',
        ],
      },
      {
        titulo: 'Servicios de terceros',
        parrafos: [
          'Algunas funciones dependen de servicios externos (Meta, Google, Flow, Amazon Web Services). No controlamos esos servicios y no somos responsables por sus interrupciones o cambios. El modo QR de WhatsApp no es oficial y puede ser limitado por WhatsApp; para uso profesional recomendamos la API oficial.',
        ],
      },
      {
        titulo: 'Tu contenido',
        parrafos: [
          'Tú eres dueño de la información que cargas y de tus conversaciones. Nos das permiso para procesarla solo con el fin de prestarte el servicio.',
        ],
      },
      {
        titulo: 'Responsabilidad',
        parrafos: [
          'El servicio se entrega "tal como está". En la medida permitida por la ley, no somos responsables por pérdidas indirectas, como ventas no concretadas, derivadas del uso del servicio o de errores de la inteligencia artificial.',
        ],
      },
      {
        titulo: 'Cambios y ley aplicable',
        parrafos: [
          'Podemos actualizar estas condiciones y te avisaremos de los cambios importantes. Estas condiciones se rigen por las leyes de la República de Chile.',
          <>Contacto: {mail}.</>,
        ],
      },
    ]}
  />
);
